import {
  type ColumnVisibilityState,
  type RowSelectionState,
  type SortingState,
  useTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  CheckIcon,
  CopyIcon,
  FileSearchIcon,
  InfoIcon,
  PartyPopperIcon,
} from 'lucide-react'
import {
  type CSSProperties,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Link } from '@/components'
import { Button, SectionHeading } from '@/components/ui'
import { BulkActionsToolbar } from '@/components/ui/table/DataTable/BulkActionsToolbar'
import { DataTableCell } from '@/components/ui/table/DataTable/DataTableCell'
import { DataTableHeaders } from '@/components/ui/table/DataTable/DataTableHeaders'
import { dataTableFeatures } from '@/components/ui/table/features'
import {
  type DuplicateGroup,
  duplicateReviewPath,
  groupDuplicates,
} from '@/modules/insights/utils'
import {
  type DuplicateRecord,
  type Transaction,
  TransactionsBulkActions,
  transactionsColumns,
  useInsightsMutations,
} from '@/modules/projects'
import { cn, formatCalendarDate, truncate } from '@/utils'

interface DuplicatesListProps {
  slug: string
  duplicates: DuplicateRecord[]
  transactions: Transaction[]
}

type FlatItem =
  | { kind: 'header'; group: DuplicateGroup; groupIndex: number }
  | { kind: 'row'; tableRowIndex: number }

const HEADER_HEIGHT = 57
const ROW_HEIGHT = 40
const MAX_DESCRIPTION_LENGTH = 60

interface DuplicatesGroupedTableProps {
  slug: string
  groups: DuplicateGroup[]
}

function DuplicatesGroupedTable({ slug, groups }: DuplicatesGroupedTableProps) {
  const { deleteDuplicates } = useInsightsMutations()

  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const columns = useMemo(() => transactionsColumns(false), [])

  const columnVisibility = useMemo<ColumnVisibilityState>(
    () =>
      columns.reduce((acc, col) => {
        if ('accessorKey' in col && !col.header) {
          acc[col.accessorKey as string] = false
        }
        return acc
      }, {} as ColumnVisibilityState),
    [columns],
  )

  const allTransactions = useMemo(
    () => groups.flatMap((g) => g.transactions),
    [groups],
  )

  const flatItems = useMemo<FlatItem[]>(() => {
    let tableRowIndex = 0
    return groups.flatMap((group, groupIndex) => [
      { kind: 'header' as const, group, groupIndex },
      ...group.transactions.map(() => ({
        kind: 'row' as const,
        tableRowIndex: tableRowIndex++,
      })),
    ])
  }, [groups])

  const table = useTable({
    features: dataTableFeatures,
    data: allTransactions,
    columns,
    getRowId: (row) => String(row.id),
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    state: { sorting, rowSelection, columnVisibility },
    // The shared features include pagination; this list shows every group on one virtualized page.
    manualPagination: true,
    columnResizeMode: 'onChange',
    defaultColumn: { size: 150, minSize: 50, maxSize: 1000 },
  })

  const { rows } = table.getRowModel()
  const parentRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  useLayoutEffect(() => {
    if (!parentRef.current) return
    const element = parentRef.current
    setContainerWidth(element.getBoundingClientRect().width)
    const observer = new ResizeObserver((entries) => {
      const newWidth = entries[0]?.contentRect.width ?? 0
      setContainerWidth((prev) =>
        Math.abs(newWidth - prev) > 1 ? newWidth : prev,
      )
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const totalWidth = table.getTotalSize()
  const tableWidth = Math.max(totalWidth, containerWidth)

  const rowVirtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) =>
      flatItems[index]?.kind === 'header' ? HEADER_HEIGHT : ROW_HEIGHT,
    overscan: 10,
  })

  const virtualItems = rowVirtualizer.getVirtualItems()
  const selectedCount = Object.values(rowSelection).filter(Boolean).length

  return (
    <>
      <div
        className="w-full rounded-lg border overflow-hidden flex flex-col"
        style={{ height: 'calc(100vh - 280px)', minHeight: '400px' }}
      >
        <div
          ref={parentRef}
          className="flex-1 min-h-0 overflow-auto scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400"
          style={
            {
              '--table-width': `${tableWidth}px`,
              '--table-container-width': `${containerWidth}px`,
            } as CSSProperties
          }
        >
          <div className="min-w-(--table-width)">
            <div className="sticky top-0 z-10 border-b bg-blue-1">
              <DataTableHeaders table={table} />
            </div>

            <div
              className="relative w-(--table-width)"
              style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
            >
              {virtualItems.map((virtualRow) => {
                const item = flatItems[virtualRow.index]
                if (!item) return null

                if (item.kind === 'header') {
                  const { group } = item
                  return (
                    <div
                      key={`header-${item.groupIndex}`}
                      data-index={virtualRow.index}
                      ref={rowVirtualizer.measureElement}
                      className="absolute left-0 top-0 flex items-center justify-between px-4 w-(--table-width) border-b bg-amber-50/70"
                      style={{
                        transform: `translateY(${virtualRow.start}px)`,
                        height: `${virtualRow.size}px`,
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center size-7 rounded-full bg-amber-50 border border-amber-200 shrink-0">
                          <CopyIcon className="size-3 text-amber-600" />
                        </div>
                        <div>
                          <p
                            className="text-sm font-semibold text-foreground leading-none mb-0.5 whitespace-nowrap"
                            title={group.description}
                          >
                            {truncate(
                              group.description,
                              MAX_DESCRIPTION_LENGTH,
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatCalendarDate(group.date)}
                            {' · $'}
                            {Math.abs(group.amount).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 sticky right-4 bg-amber-50/70 pl-3">
                        <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                          {group.transactions.length} matches
                        </span>
                        <Button
                          theme="outline"
                          size="sm"
                          asChild
                          className="h-7 gap-1.5 text-xs"
                        >
                          <Link
                            to={duplicateReviewPath(slug, group.id)}
                            preload="intent"
                            className="no-underline hover:no-underline"
                          >
                            <FileSearchIcon className="size-3.5" />
                            Review
                          </Link>
                        </Button>
                        <Button
                          theme="outline"
                          size="sm"
                          onClick={() =>
                            deleteDuplicates(
                              slug,
                              group.transactions.map(({ id }) => id),
                            )
                          }
                          className="h-7 gap-1.5 text-xs"
                        >
                          <CheckIcon className="size-3.5" />
                          Mark as reviewed
                        </Button>
                      </div>
                    </div>
                  )
                }

                const row = rows[item.tableRowIndex]
                if (!row) return null
                const isSelected = row.getIsSelected()

                return (
                  <div
                    key={row.id}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    className={cn(
                      'absolute left-0 top-0 flex items-center px-4 w-(--table-width) border-b border-border/80',
                      isSelected ? 'bg-blue-50' : 'hover:bg-blue-1',
                    )}
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                      height: `${virtualRow.size}px`,
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <DataTableCell key={cell.id} cell={cell} />
                    ))}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      <BulkActionsToolbar
        selectedCount={selectedCount}
        onClose={() => table.resetRowSelection()}
      >
        <TransactionsBulkActions
          selection={rowSelection}
          transactions={allTransactions}
        />
      </BulkActionsToolbar>
    </>
  )
}

export function DuplicatesList({
  slug,
  duplicates,
  transactions,
}: DuplicatesListProps) {
  const groups = useMemo(
    () => groupDuplicates(duplicates, transactions),
    [duplicates, transactions],
  )

  const hasDuplicates = groups.length > 0

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <SectionHeading
          title="Duplicate transactions"
          description="Transactions flagged as potential duplicates"
        />
        {hasDuplicates && (
          <div className="flex gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
            <InfoIcon className="size-4 shrink-0 text-blue-600 mt-0.5" />
            <p className="text-xs leading-relaxed text-blue-800">
              These matches are identified through pattern detection and may
              include legitimate repeat transactions. Review each group and
              delete only the entries you can confirm are true duplicates.{' '}
              <span className="font-semibold">
                Once transactions are deleted, they cannot be recovered.
              </span>
            </p>
          </div>
        )}
      </div>
      {!hasDuplicates ? (
        <div className="flex items-center gap-3 py-2">
          <PartyPopperIcon className="size-5 shrink-0 text-emerald-500" />
          <div>
            <p className="text-sm font-semibold text-foreground">
              No duplicates found
            </p>
            <p className="text-xs text-muted-foreground">
              Every transaction in this project looks unique.
            </p>
          </div>
        </div>
      ) : (
        <DuplicatesGroupedTable slug={slug} groups={groups} />
      )}
    </div>
  )
}
